import React from "react";
import { Box, Typography, Grid, Avatar, CardContent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import GlassPanel from "./ui/GlassPanel";
import TiltCard from "./ui/TiltCard";

const services = [
  {
    name: "SEO",
    position: "Search Engine Optimization",
    description: "Get higher rankings and more traffic with smart keywords, quality links, and optimized content.",
    image: "./assets/seoR.png",
    link: "/services/seo",
  },
  {
    name: "Social Media Marketing",
    position: "Marketing & Branding",
    description: "Grow your brand with engaging content, targeted ads, and the right audience.",
    image: "./assets/socialmedia.png",
    link: "/services/social-media-marketing",
  },
  {
    name: "PPC Advertising",
    position: "Google & YouTube Ads",
    description: "We help you get more leads and sales with engaging ads and smart budget optimization.",
    image: "./assets/ppc.jpg",
    link: "/services/google-ads",
  },
  {
    name: "Web Development",
    position: "Design & Development",
    description: "We build high-performing websites, from design to maintenance, to help your business grow online.",
    image: "./assets/web.png",
    link: "/services/website-development",
  },
];

export default function Hero6() {
  const navigate = useNavigate();

  return (
    <Box
      component="section"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: { xs: 2, md: 5 },
        py: { xs: 8, md: 12 },
        gap: 6,
        background: "linear-gradient(180deg, #ffffff 0%, #FBF6F1 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: "-10%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 700,
          height: 700,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(253,129,18,0.08) 0%, rgba(253,129,18,0) 70%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Header */}
      <Box sx={{ maxWidth: "1100px", textAlign: "center", position: "relative", zIndex: 1 }}>
        <Typography
          component="h2"
          variant="h3"
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontWeight: 700,
            color: "#14141F",
            mb: 2,
          }}
        >
          Our Services
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#5B5B6E",
            fontFamily: "Nunito, serif",
            fontSize: "1.1rem",
          }}
        >
          We Provide a Wide Range of Services to Help You Grow Your Business
        </Typography>
      </Box>

      {/* Services Grid */}
      <Grid container spacing={4} sx={{ maxWidth: "1100px", justifyContent: "center", position: "relative", zIndex: 1 }}>
        {services.map((service, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              style={{ height: "100%" }}
            >
             <TiltCard glareColor="#FD8112" maxTilt={8}>
              <GlassPanel
                role="button"
                aria-label={`Learn more about ${service.name}`}
                onClick={() => navigate(service.link)}
                sx={{
                  height: 320,
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  position: "relative",
                  background: "rgba(255,255,255,0.9)",
                  border: "1px solid rgba(253,129,18,0.15)",
                  boxShadow: "0 10px 30px rgba(20,20,40,0.06)",
                  transition: "box-shadow 0.35s ease, border-color 0.35s ease",
                  "&:hover": {
                    boxShadow: "0 24px 50px rgba(253,129,18,0.22)",
                    borderColor: "rgba(253,129,18,0.5)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 3,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "100%",
                    justifyContent: "space-between",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <Box>
                    <Avatar
                      src={service.image}
                      alt={`${service.name} Service`}
                      loading="lazy"
                      sx={{
                        width: 80,
                        height: 80,
                        mb: 2,
                        mx: "auto",
                        bgcolor: "#fff",
                        boxShadow: "0 10px 24px rgba(253,129,18,0.25)",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "rotate(8deg) scale(1.08)",
                        },
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        fontFamily: "Poppins, sans-serif",
                        color: "#14141F",
                        mb: 1,
                      }}
                    >
                      {service.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#FD8112",
                        mb: 2,
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      {service.position}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#5B5B6E",
                      fontFamily: "Nunito, serif",
                      lineHeight: 1.6,
                    }}
                  >
                    {service.description}
                  </Typography>
                </CardContent>
              </GlassPanel>
             </TiltCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
